import { memo, type KeyboardEvent } from "react";
import type { CostCenter, FixedCost, Occurrence } from "../types.ts";
import { CurrencyInput } from "./CurrencyInput.tsx";

type Props = {
  cost: FixedCost;
  costCenters: CostCenter[];
  onPatch: (id: string, patch: Partial<FixedCost>) => void;
  onDelete: (id: string) => void;
  onAddAfter: (afterId: string) => void;
};

const OCCURRENCE_OPTIONS: Array<{ value: Occurrence; label: string }> = [
  { value: "monthly", label: "Månad" },
  { value: "quarterly", label: "Kvartal" },
  { value: "yearly", label: "År" },
];

const MONTH_OPTIONS = [
  "jan",
  "feb",
  "mar",
  "apr",
  "maj",
  "jun",
  "jul",
  "aug",
  "sep",
  "okt",
  "nov",
  "dec",
];

function siblingRow(tr: Element, dir: "next" | "prev"): HTMLElement | null {
  let s = dir === "next" ? tr.nextElementSibling : tr.previousElementSibling;
  while (s && !s.classList.contains("fixed-cost-row")) {
    s = dir === "next" ? s.nextElementSibling : s.previousElementSibling;
  }
  return s as HTMLElement | null;
}

function fieldsIn(tr: Element): HTMLElement[] {
  return Array.from(tr.querySelectorAll<HTMLElement>("[data-field]"));
}

function focusFieldInRow(tr: Element, field: string): boolean {
  const el = tr.querySelector<HTMLElement>(
    `[data-field="${CSS.escape(field)}"]`,
  );
  if (!el) return false;
  el.focus();
  if (el instanceof HTMLInputElement && el.type === "text") el.select();
  return true;
}

function isAtTextStart(el: HTMLInputElement): boolean {
  return el.selectionStart === 0 && el.selectionEnd === 0;
}
function isAtTextEnd(el: HTMLInputElement): boolean {
  return (
    el.selectionStart === el.value.length && el.selectionEnd === el.value.length
  );
}

function FixedCostsRowImpl({
  cost,
  costCenters,
  onPatch,
  onDelete,
  onAddAfter,
}: Props) {
  const amountClass =
    "col-amount num amount" +
    (cost.amount < 0 ? " amount-neg" : cost.amount > 0 ? " amount-pos" : "");

  const handleNav = (
    e: KeyboardEvent<HTMLInputElement | HTMLSelectElement>,
    field: string,
  ) => {
    const el = e.currentTarget;
    const tr = el.closest("tr.fixed-cost-row");
    if (!tr) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = siblingRow(tr, "next");
      if (next) focusFieldInRow(next, field);
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      const prev = siblingRow(tr, "prev");
      if (prev) focusFieldInRow(prev, field);
      return;
    }

    const isText = el instanceof HTMLInputElement && el.type === "text";
    const atStart = !isText || isAtTextStart(el);
    const atEnd = !isText || isAtTextEnd(el);

    if (e.key === "ArrowLeft" && atStart) {
      const fields = fieldsIn(tr);
      const i = fields.indexOf(el);
      const target = i > 0 ? fields[i - 1] : null;
      if (target) {
        e.preventDefault();
        target.focus();
        if (target instanceof HTMLInputElement && target.type === "text") {
          target.select();
        }
      }
    } else if (e.key === "ArrowRight" && atEnd) {
      const fields = fieldsIn(tr);
      const i = fields.indexOf(el);
      const target = i >= 0 && i < fields.length - 1 ? fields[i + 1] : null;
      if (target) {
        e.preventDefault();
        target.focus();
        if (target instanceof HTMLInputElement && target.type === "text") {
          target.select();
        }
      }
    }
  };

  return (
    <tr className="row fixed-cost-row" data-cost-id={cost.id}>
      <td className="col-description">
        <input
          type="text"
          data-field="description"
          value={cost.description}
          onChange={(e) =>
            onPatch(cost.id, { description: e.currentTarget.value })
          }
          onKeyDown={(e) => handleNav(e, "description")}
        />
      </td>
      <td className="col-category">
        <input
          type="text"
          data-field="category"
          value={cost.category}
          onChange={(e) =>
            onPatch(cost.id, { category: e.currentTarget.value })
          }
          onKeyDown={(e) => handleNav(e, "category")}
        />
      </td>
      <td className="col-cost-center">
        <select
          data-field="costCenterId"
          aria-label="Kostnadsställe"
          value={cost.costCenterId ?? ""}
          onChange={(e) =>
            onPatch(cost.id, { costCenterId: e.currentTarget.value || null })
          }
          onKeyDown={(e) => handleNav(e, "costCenterId")}
        >
          <option value="">—</option>
          {costCenters.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </td>
      <td className="col-day num">
        <input
          type="number"
          data-field="dayOfMonth"
          min={1}
          max={31}
          value={cost.dayOfMonth}
          onChange={(e) => {
            const n = Number(e.currentTarget.value);
            if (Number.isFinite(n)) {
              onPatch(cost.id, {
                dayOfMonth: Math.max(1, Math.min(31, Math.round(n))),
              });
            }
          }}
          onKeyDown={(e) => handleNav(e, "dayOfMonth")}
        />
      </td>
      <td className="col-occurrence">
        <div className="occurrence-controls">
          <select
            data-field="occurrence"
            aria-label="Frekvens"
            value={cost.occurrence}
            onChange={(e) => {
              const occurrence = e.currentTarget.value as Occurrence;
              onPatch(cost.id, {
                occurrence,
                startMonth:
                  occurrence === "monthly"
                    ? null
                    : (cost.startMonth ?? new Date().getMonth() + 1),
              });
            }}
            onKeyDown={(e) => handleNav(e, "occurrence")}
          >
            {OCCURRENCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {cost.occurrence !== "monthly" && (
            <select
              data-field="startMonth"
              aria-label="Startmånad"
              title="Räknas från denna månad"
              value={cost.startMonth ?? 1}
              onChange={(e) =>
                onPatch(cost.id, {
                  startMonth: Number(e.currentTarget.value),
                })
              }
              onKeyDown={(e) => handleNav(e, "startMonth")}
            >
              {MONTH_OPTIONS.map((label, i) => (
                <option key={label} value={i + 1}>
                  från {label}
                </option>
              ))}
            </select>
          )}
        </div>
      </td>
      <td className={amountClass}>
        <CurrencyInput
          value={cost.amount}
          data-field="amount"
          onChange={(n) => onPatch(cost.id, { amount: n })}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onAddAfter(cost.id);
              return;
            }
            handleNav(e, "amount");
          }}
        />
      </td>
      <td className="col-delete">
        <button
          className="delete-btn"
          tabIndex={-1}
          title="Ta bort kostnad"
          onClick={() => onDelete(cost.id)}
        >
          ×
        </button>
      </td>
    </tr>
  );
}

export const FixedCostsRow = memo(FixedCostsRowImpl);
